import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  Input,
  TextArea,
  FileUpload,
  DataTable,
  Skeleton,
  EmptyState,
  DatePicker,
  Toggle,
  Alert,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useSocialPosts,
  useCreateSocialPost,
  useActiveProducts,
} from '@/features/communications';
import type { SocialPost, SocialPlatform, ProductOption } from '@/features/communications';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { getSubunitMultiplier } from '@bbpos/utils';
import type { CurrencyCode } from '@bbpos/types';
import { buildSocialPostPayload, fileToDataUrl } from './social-post-payload';
import { uploadSocialImage } from './social-image-upload';

const PLATFORM_OPTIONS = [
  { value: 'INSTAGRAM', label: 'Instagram' },
  { value: 'FACEBOOK', label: 'Facebook' },
];

const STATUS_COLORS: Record<string, string> = {
  PUBLISHED: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300',
  SCHEDULED: 'bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300',
  DRAFT: 'bg-neutral-100 text-neutral-600 dark:bg-neutral-700/60 dark:text-neutral-300',
  FAILED: 'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-300',
};

type Row = SocialPost;

// Currency-aware money formatter. Symbol and subunit factor come from the
// shared single-source helpers (`getCurrencySymbol` / `getSubunitMultiplier`);
// the no-decimal `en-IN` grouping is preserved verbatim so the INR path stays
// byte-identical to the previous inline implementation.
function fmtPaise(p: number, currency: CurrencyCode): string {
  const major = p / getSubunitMultiplier(currency);
  return `${getCurrencySymbol(currency)}${major.toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
}

function SocialMediaContent() {
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Form state
  const [platform, setPlatform] = useState<SocialPlatform>('INSTAGRAM');
  const [productId, setProductId] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string | null>(null);
  // The uploaded image's hosted S3 URL — sent as `image_url` so the post carries
  // a real URL Instagram/Facebook can fetch (not a base64 data: URL). See #141.
  const [uploadedImageHostedUrl, setUploadedImageHostedUrl] = useState<string | null>(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [authError, setAuthError] = useState(false);

  const { data: products = [], isLoading: productsLoading } = useActiveProducts();
  const createMutation = useCreateSocialPost();

  const postPage = Number(searchParams.get('page') ?? '1');
  const { data: postsData, isLoading: postsLoading, isFetching } = useSocialPosts(postPage);
  const postRows = useMemo<Row[]>(() => postsData?.data ?? [], [postsData]);

  const productOptions = useMemo(
    () => [
      { value: '', label: '— Select a product (optional) —' },
      ...products.map((p: ProductOption) => ({
        value: p.id,
        label: `${p.name}${p.selling_price ? ` (${fmtPaise(p.selling_price, currency)})` : ''}`,
      })),
    ],
    [products, currency],
  );

  const selectedProduct = useMemo(
    () => products.find((p: ProductOption) => p.id === productId),
    [products, productId],
  );

  // Keyboard: Ctrl+Enter → publish
  const canPublishRef = useRef(false);
  canPublishRef.current = caption.trim().length > 0;
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canPublishRef.current) {
        e.preventDefault();
        handlePublish();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleImageUpload = useCallback(
    async (files: File[]) => {
      const file = files[0];
      if (!file) return;
      setImageUploading(true);
      try {
        // Show an instant local preview while the real upload runs.
        setUploadedImageUrl(await fileToDataUrl(file));
        // Upload to S3 so the post carries a hosted URL the platforms can fetch.
        setUploadedImageHostedUrl(await uploadSocialImage(file));
      } catch {
        setUploadedImageHostedUrl(null);
        toast({ variant: 'error', title: 'Could not upload image', description: 'Please try a different file.' });
      } finally {
        setImageUploading(false);
      }
    },
    [toast],
  );

  const handlePublish = useCallback(() => {
    if (!caption.trim()) {
      toast({ variant: 'warning', title: 'Caption required' });
      return;
    }
    if (imageUploading) {
      toast({ variant: 'warning', title: 'Image still uploading', description: 'Please wait for the image to finish uploading.' });
      return;
    }

    createMutation.mutate(
      buildSocialPostPayload({
        platform,
        caption,
        productId,
        uploadedImageUrl: uploadedImageHostedUrl,
        productImageUrl: selectedProduct?.image_url,
        isScheduled,
        scheduledAt,
      }),
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: isScheduled ? 'Post scheduled' : 'Post published',
            description: `Your ${platform.toLowerCase()} post is ${isScheduled ? 'scheduled' : 'live'}.`,
          });
          setCaption('');
          setHashtags('');
          setProductId('');
          setUploadedImageUrl(null);
          setUploadedImageHostedUrl(null);
          setIsScheduled(false);
          setScheduledAt(undefined);
          setAuthError(false);
        },
        onError: (err) => {
          const msg = (err as Error).message ?? '';
          if (msg.includes('auth') || msg.includes('reconnect')) {
            setAuthError(true);
          }
          toast({ variant: 'error', title: 'Failed to publish', description: msg || 'Please try again.' });
        },
      },
    );
  }, [platform, productId, caption, hashtags, uploadedImageHostedUrl, imageUploading, selectedProduct, isScheduled, scheduledAt, createMutation, toast]);

  const postColumns: ColumnDef<Row>[] = useMemo(() => [
    {
      key: 'platform',
      header: 'Platform',
      render: (_v, row) => (
        <span className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider ${
          row.platform === 'INSTAGRAM' ? 'text-pink-600 dark:text-pink-400' : 'text-indigo-600 dark:text-indigo-400'
        }`}>
          {row.platform === 'INSTAGRAM' ? (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zm1.5-4.87h.01M6.5 3h11A3.5 3.5 0 0121 6.5v11a3.5 3.5 0 01-3.5 3.5h-11A3.5 3.5 0 013 17.5v-11A3.5 3.5 0 016.5 3z" />
            </svg>
          ) : (
            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
            </svg>
          )}
          {row.platform}
        </span>
      ),
    },
    {
      key: 'caption',
      header: 'Caption',
      render: (_v, row) => (
        <p className="text-sm text-neutral-700 dark:text-neutral-200 line-clamp-1 max-w-[200px]">
          {row.caption}
        </p>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_COLORS[row.status] ?? STATUS_COLORS.DRAFT}`}>
          {row.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_v, row) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
          {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
  ], []);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-pink-500 via-rose-500 to-orange-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(236,72,153,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
            Social Media
          </span>
          <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Post Creator</h1>
          <p className="mt-1.5 text-sm text-white/90 max-w-xl">
            Create promotional posts for Instagram and Facebook directly from your product catalog.
          </p>
        </div>
      </div>

      {/* Auth error alert */}
      {authError ? (
        <Alert variant="danger" title="Social media auth failed" description="Please reconnect your social media account in Settings before publishing." className="mb-6" />
      ) : null}

      {/* Form + Preview */}
      <div className="relative z-20 grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Form — 2/3 */}
        <div className="lg:col-span-2 rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg p-6">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-5 border-l-4 border-pink-500 pl-3">
            Create Post
          </h2>

          <div className="space-y-5">
            <Select
              label="Platform"
              options={PLATFORM_OPTIONS}
              value={platform}
              onChange={(v) => setPlatform(v as SocialPlatform)}
            />

            <Select
              label="Product (optional)"
              options={productOptions}
              value={productId}
              onChange={(v) => setProductId(typeof v === 'string' ? v : '')}
              disabled={productsLoading}
            />

            <div>
              <label className="mb-1.5 block text-sm font-medium text-neutral-700 dark:text-neutral-200">
                Image (optional)
              </label>
              <FileUpload
                accept="image/*"
                maxSize={5 * 1024 * 1024}
                variant="dropzone"
                preview={uploadedImageUrl}
                onUpload={handleImageUpload}
              />
              {uploadedImageUrl ? (
                <button
                  type="button"
                  onClick={() => { setUploadedImageUrl(null); setUploadedImageHostedUrl(null); }}
                  className="mt-2 text-xs font-medium text-pink-600 hover:text-pink-700 dark:text-pink-400"
                >
                  Remove image
                </button>
              ) : (
                <p className="mt-1.5 text-[11px] text-neutral-400">
                  Upload a custom picture, or pick a product to use its image. Max 5MB.
                </p>
              )}
            </div>

            <TextArea
              label="Caption"
              value={caption}
              onChange={setCaption}
              placeholder="Write your post caption..."
              rows={4}
              maxLength={2200}
              required
            />

            <Input
              label="Hashtags"
              value={hashtags}
              onChange={setHashtags}
              placeholder="#Orio360 #DealOfTheDay #ShopLocal"
            />

            <div className="flex items-center justify-between rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 dark:from-pink-900/20 dark:to-rose-900/20 p-4 shadow-sm">
              <div>
                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Schedule Post</p>
                <p className="mt-0.5 text-[11px] text-neutral-500 dark:text-neutral-400">
                  Set a future date and time to publish
                </p>
              </div>
              <Toggle checked={isScheduled} onChange={setIsScheduled} />
            </div>

            {isScheduled ? (
              <DatePicker
                label="Schedule Date"
                value={scheduledAt}
                onChange={setScheduledAt}
                placeholder="Select date and time"
                minDate={new Date()}
              />
            ) : null}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                variant="primary"
                onClick={handlePublish}
                loading={createMutation.isPending || imageUploading}
                disabled={!caption.trim() || imageUploading}
              >
                {isScheduled ? 'Schedule' : 'Publish Now'}
              </Button>
              <span className="text-[11px] text-neutral-400 hidden sm:inline">Ctrl+Enter</span>
            </div>
          </div>
        </div>

        {/* Preview — 1/3 */}
        <div className="rounded-2xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-sm shadow-lg p-6 sticky top-6 self-start">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-900 dark:text-neutral-100 mb-4 border-l-4 border-pink-500 pl-3">
            Post Preview
          </h2>

          <div className="rounded-xl overflow-hidden bg-white dark:bg-neutral-700/40 shadow-md">
            {/* Image area */}
            <div className="aspect-square bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/20 dark:to-rose-900/20 flex items-center justify-center">
              {uploadedImageUrl || selectedProduct?.image_url ? (
                <img src={uploadedImageUrl ?? selectedProduct?.image_url ?? ''} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="text-center p-6">
                  <svg className="mx-auto h-12 w-12 text-pink-300 dark:text-pink-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="mt-2 text-xs text-neutral-400">Select a product or upload an image</p>
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-4">
              {selectedProduct ? (
                <div className="mb-3">
                  <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100">{selectedProduct.name}</p>
                  {selectedProduct.selling_price ? (
                    <p className="text-lg font-bold text-success-600 dark:text-success-400 font-mono tabular-nums">
                      {fmtPaise(selectedProduct.selling_price, currency)}
                    </p>
                  ) : null}
                </div>
              ) : null}

              <p className="text-sm text-neutral-700 dark:text-neutral-200 whitespace-pre-wrap break-words line-clamp-4">
                {caption || 'Your caption will appear here...'}
              </p>

              {hashtags.trim() ? (
                <p className="mt-2 text-xs text-brand-500 dark:text-brand-400">
                  {hashtags}
                </p>
              ) : null}

              <div className="mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-600/50 flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider ${
                  platform === 'INSTAGRAM' ? 'text-pink-500' : 'text-indigo-500'
                }`}>
                  {platform}
                </span>
                {isScheduled && scheduledAt ? (
                  <span className="text-[10px] text-neutral-400 font-mono tabular-nums">
                    Scheduled: {scheduledAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Post History */}
      <h2 className="text-lg font-bold text-neutral-900 dark:text-neutral-100 mb-4">Recent Posts</h2>
      {postsLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : postRows.length === 0 ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No social posts yet"
            description="Create your first post to promote products on Instagram and Facebook."
            action={{ label: 'Create Your First Post', onClick: () => window.scrollTo({ top: 0, behavior: 'smooth' }) }}
            icon={
              <svg className="h-20 w-20 text-pink-200 dark:text-pink-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37zm1.5-4.87h.01M6.5 3h11A3.5 3.5 0 0121 6.5v11a3.5 3.5 0 01-3.5 3.5h-11A3.5 3.5 0 013 17.5v-11A3.5 3.5 0 016.5 3z" />
              </svg>
            }
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <section className="hidden sm:block rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Social media post history">
            <DataTable<Row>
              columns={postColumns}
              data={postRows}
              loading={isFetching}
              totalCount={postsData?.meta.total}
              currentPage={postPage}
              pageSize={20}
              onPageChange={(p) => {
                const params = new URLSearchParams(searchParams);
                params.set('page', String(p));
                setSearchParams(params);
              }}
              stickyHeader
            />
          </section>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3 animate-slide-up">
            {postRows.map((post) => (
              <div key={post.id} className="rounded-xl bg-white dark:bg-neutral-800 shadow-md p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-semibold uppercase tracking-wider ${
                    post.platform === 'INSTAGRAM' ? 'text-pink-600 dark:text-pink-400' : 'text-indigo-600 dark:text-indigo-400'
                  }`}>
                    {post.platform}
                  </span>
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_COLORS[post.status] ?? STATUS_COLORS.DRAFT}`}>
                    {post.status}
                  </span>
                </div>
                <p className="text-sm text-neutral-700 dark:text-neutral-200 line-clamp-2">{post.caption}</p>
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
                  {new Date(post.created_at).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                </p>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function SocialMediaPostCreatorPage() {
  return (
    <PageContainer
      title="Social Media"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Social Media' },
      ]}
    >
      <ErrorBoundary resetKey="comm-social-media">
        <SocialMediaContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
